/**
 * German formatting for the admin application.
 *
 * A sibling of apps/web/src/i18n/format.ts, not an import of it — same
 * reasoning as de.ts (see its own comment): the two applications ship
 * independently, so this is its own small copy rather than a shared runtime
 * dependency. Only what this app's dashboard, user table and dialogs
 * actually need — not a restatement of the customer app's whole surface.
 */

const LOCALE = 'de-DE'

const integer = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 })
const dateShort = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
const dateTime = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value)
}

/** "1.284" — a plain count, German thousands separator. Never money. */
export function formatCount(value: number): string {
  return integer.format(value)
}

export function formatDate(value: Date | string): string {
  return dateShort.format(toDate(value))
}

export function formatDateTime(value: Date | string): string {
  return dateTime.format(toDate(value))
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
]

const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' })

/** "vor 7 Stunden" — used for "zuletzt aktiv" and a data source's last run. */
export function formatRelative(value: Date | string, now: Date = new Date()): string {
  const elapsed = toDate(value).getTime() - now.getTime()
  const magnitude = Math.abs(elapsed)
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (magnitude >= ms) return relative.format(Math.round(elapsed / ms), unit)
  }
  return relative.format(Math.round(elapsed / 1000), 'second')
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const
const bytesFormatter = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 })

/** Human-sized figure for §5's "Belegung des Ablageverzeichnisses", German-formatted (comma decimal). */
export function formatBytes(bytes: number): string {
  let value = bytes
  let unitIndex = 0
  while (value >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  return `${bytesFormatter.format(value)} ${BYTE_UNITS[unitIndex]}`
}

/** Picks singular or plural. German has no zero form, so zero takes plural. */
export function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many
}
